import { useCallback, useEffect, useRef, useState } from "react";
import type { AdminLogEntry } from "@/server/admin/logEntries";
import { adminFetch, adminLiveUrl, getAdminClientSecret } from "@/utils/adminAuth";
import type { AdminLiveEvent, AgentRunRecord } from "../utils/types";

const LOG_LIMIT = 500;
const MAX_RECONNECT_MS = 30_000;

type UseAdminLiveInput = {
  onMetricsTick?: () => void;
  phone?: string;
  enabled?: boolean;
};

const appendLog = (current: AdminLogEntry[], entry: AdminLogEntry) => {
  if (current.some((existing) => existing.id === entry.id)) {
    return current;
  }

  return [entry, ...current].slice(0, LOG_LIMIT);
};

export const useAdminLive = ({
  onMetricsTick,
  phone = "",
  enabled = true,
}: UseAdminLiveInput = {}) => {
  const [logs, setLogs] = useState<AdminLogEntry[]>([]);
  const [activeRuns, setActiveRuns] = useState<AgentRunRecord[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onMetricsTickRef = useRef(onMetricsTick);
  const hasConnectedRef = useRef(false);

  onMetricsTickRef.current = onMetricsTick;

  const loadInitial = useCallback(async () => {
    if (!enabled) return;

    const logParams = new URLSearchParams({ limit: String(LOG_LIMIT) });
    const phoneQuery = phone.trim();

    if (phoneQuery) {
      logParams.set("phone", phoneQuery);
    }

    const [logsResponse, runsResponse] = await Promise.all([
      adminFetch(`/api/admin/logs?${logParams.toString()}`),
      adminFetch("/api/admin/runs/active"),
    ]);

    if (!logsResponse.ok) {
      setError(`Admin logs request failed (${logsResponse.status})`);
      return;
    }

    if (!runsResponse.ok) {
      setError(`Active runs request failed (${runsResponse.status})`);
      return;
    }

    const logsBody = await logsResponse.json() as { logs: AdminLogEntry[] };
    const runsBody = await runsResponse.json() as { runs: AgentRunRecord[] };

    setLogs(logsBody.logs);
    setActiveRuns(runsBody.runs);
    setError(null);
  }, [enabled, phone]);

  const handleLiveEvent = useCallback((payload: AdminLiveEvent) => {
    switch (payload.kind) {
      case "log":
        setLogs((current) => appendLog(current, payload.entry));
        break;
      case "run":
        if (payload.run.status === "started") {
          setActiveRuns((current) => {
            const without = current.filter((run) => run.id !== payload.run.id);
            return [payload.run, ...without];
          });
        } else {
          setActiveRuns((current) => current.filter((run) => run.id !== payload.run.id));
        }
        break;
      case "metrics_tick":
        onMetricsTickRef.current?.();
        break;
      default: {
        const _exhaustive: never = payload;
        void _exhaustive;
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    void loadInitial();
  }, [enabled, loadInitial]);

  useEffect(() => {
    if (!enabled) return;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelayMs = 1_000;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      if (!getAdminClientSecret()) {
        setError("Admin live stream unavailable — missing auth secret.");
        return;
      }

      source = new EventSource(adminLiveUrl());

      source.onopen = () => {
        setConnected(true);
        setError(null);
        reconnectDelayMs = 1_000;

        if (hasConnectedRef.current) {
          void loadInitial();
        } else {
          hasConnectedRef.current = true;
        }
      };

      source.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data) as AdminLiveEvent;
          handleLiveEvent(payload);
        } catch {
          // Ignore malformed SSE payloads.
        }
      };

      source.onerror = () => {
        setConnected(false);
        source?.close();
        source = null;

        if (cancelled) return;

        if (!getAdminClientSecret()) {
          setError("Admin live stream unauthorized — check ADMIN_INGEST_SECRET.");
          return;
        }

        reconnectTimer = setTimeout(() => {
          reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_MS);
          connect();
        }, reconnectDelayMs);
      };
    };

    connect();

    return () => {
      cancelled = true;
      hasConnectedRef.current = false;

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      source?.close();
      setConnected(false);
    };
  }, [enabled, handleLiveEvent, loadInitial]);

  return { logs, activeRuns, connected, error, reload: loadInitial };
};
